import {OTLPTraceExporter} from '@opentelemetry/exporter-trace-otlp-http';
import {BatchSpanProcessor, WebTracerProvider} from '@opentelemetry/sdk-trace-web';
import {DocumentLoadInstrumentation} from '@opentelemetry/instrumentation-document-load';
import {UserInteractionInstrumentation} from '@opentelemetry/instrumentation-user-interaction';
import {XMLHttpRequestInstrumentation} from '@opentelemetry/instrumentation-xml-http-request';
import {ZoneContextManager} from '@opentelemetry/context-zone';
import {registerInstrumentations} from '@opentelemetry/instrumentation';
import {resourceFromAttributes} from '@opentelemetry/resources';
import {ATTR_SERVICE_NAME} from '@opentelemetry/semantic-conventions';
import {getTracingDomain} from './services/envs';
import {useEffect, useRef} from 'react';
import {context, trace} from '@opentelemetry/api';
import {useLocation} from 'react-router-dom';

const collectorOptions = {
  url: getTracingDomain() + '/v1/traces', // url is optional and can be omitted - default is http://localhost:4318/v1/traces
  headers: {}, // an optional object containing custom headers to be sent with each request
  concurrencyLimit: 10, // an optional limit on pending requests
};

const exporter = new OTLPTraceExporter(collectorOptions);

const provider = new WebTracerProvider({
  resource: resourceFromAttributes({
    [ATTR_SERVICE_NAME]: 'portw',
  }),
  spanProcessors: [
    // new SimpleSpanProcessor(new ConsoleSpanExporter()),
    new BatchSpanProcessor(exporter, {
      // The maximum queue size. After the size is reached, spans are dropped.
      maxQueueSize: 100,
      // The maximum batch size of every export. It must be smaller or equal to maxQueueSize.
      maxExportBatchSize: 10,
      // The interval between two consecutive exports
      scheduledDelayMillis: 500,
      // How long the export can run before it is canceled
      exportTimeoutMillis: 30000,
    }),
  ],
});

provider.register({
  // Changing default contextManager to use ZoneContextManager - supports asynchronous operations - optional
  contextManager: new ZoneContextManager(),
});

// Registering instrumentations
registerInstrumentations({
  instrumentations: [
    new DocumentLoadInstrumentation(),
    new UserInteractionInstrumentation(),
    new XMLHttpRequestInstrumentation({
      propagateTraceHeaderCorsUrls: [new RegExp('http:\\/\\/.*\\.payactive\\.eu'), new RegExp('https+:\\/\\/.*localhost.*')],
    }),
  ],
});

export function usePageSpan(spanName: string) {
  useEffect(() => {
    const tracer = trace.getTracer('portw');
    const span = tracer.startSpan(spanName);
    const ctx = trace.setSpan(context.active(), span);

    context.with(ctx, () => {
      // Set context globally so all following Axios calls have correct traceparent
    });

    return () => span.end();
  }, [spanName]);
}

export const RouteTracer = () => {
  const location = useLocation();
  const activeSpan = useRef<any>(null);

  useEffect(() => {
    activeSpan.current?.end();
    const tracer = trace.getTracer('portw');
    const span = tracer.startSpan(`Route:${location.pathname}`);
    const ctx = trace.setSpan(context.active(), span);

    context.with(ctx, () => {
      span.setAttribute('url', location.pathname);
    });
    activeSpan.current = span;

    return () => {
      span.end();
    };
  }, [location]);

  return null;
};
